/**
 * Hình orb. Game chỉ gọi theo logical key (`orbKey`), đúng quy ước reskin của contract
 * (`orb.R.basic`, `booster.rocketH.R`, …). Khi packages/assets (s8) có manifest + atlas thì
 * đăng ký `setArtResolver` trả Texture theo key; chưa có thì vẽ placeholder bằng Graphics
 * (hình theo màu cho người mù màu, core-loop 3.2). Placeholder vẽ trong khung 24 × 24
 * (ORB_SRC_PX) tâm ở (0,0), view phóng theo `orbScale` nguyên.
 */
import { GraphicsContext, type Texture } from 'pixi.js';
import type { Cell, Overlay } from '@ml/core';
import type { Color } from '@ml/config';

export function orbKey(cell: Cell): string {
  switch (cell.t) {
    case 'orb':
      return `orb.${cell.color}.${cell.kind}`;
    case 'booster':
      return `booster.${cell.kind}.${cell.color}`;
    case 'lightball':
      return 'orb.lightball';
    case 'essence':
      return 'orb.essence';
  }
}

export function overlayKey(o: Overlay): string {
  return o.type === 'freeze' ? `overlay.freeze.${Math.min(o.layers, 3)}` : `overlay.${o.type}`;
}

export const COLOR_HEX: Record<Color, number> = {
  R: 0xe0473c,
  B: 0x3b7be0,
  G: 0x43b54a,
  Y: 0xf2c53d,
  P: 0x9a52d6,
};

type Resolver = (key: string) => Texture | null;
let resolver: Resolver | null = null;
/** Hook cho skin thật (s8). Trả null để rơi về placeholder. */
export function setArtResolver(r: Resolver | null): void {
  resolver = r;
}
export function textureFor(key: string): Texture | null {
  return resolver ? resolver(key) : null;
}

const cache = new Map<string, GraphicsContext>();

function poly(ctx: GraphicsContext, n: number, r: number, rot: number): GraphicsContext {
  const pts: number[] = [];
  for (let i = 0; i < n; i++) {
    const a = rot + (i * 2 * Math.PI) / n;
    pts.push(Math.round(Math.cos(a) * r), Math.round(Math.sin(a) * r));
  }
  return ctx.poly(pts);
}

function colorShape(ctx: GraphicsContext, color: Color, r: number): GraphicsContext {
  switch (color) {
    case 'R':
      return poly(ctx, 3, r + 1, -Math.PI / 2);
    case 'B':
      return ctx.circle(0, 0, r);
    case 'G':
      return ctx.rect(-r + 1, -r + 1, 2 * r - 2, 2 * r - 2);
    case 'Y':
      return poly(ctx, 4, r + 1, -Math.PI / 2);
    case 'P':
      return poly(ctx, 6, r, 0);
  }
}

function drawOrb(cell: Cell): GraphicsContext {
  const g = new GraphicsContext();
  const dark = 0x15151f;
  if (cell.t === 'orb') {
    const hex = COLOR_HEX[cell.color];
    colorShape(g, cell.color, 10).fill(hex).stroke({ width: 2, color: dark });
    switch (cell.kind) {
      case 'kin':
        g.circle(0, 0, 4).stroke({ width: 2, color: 0xffffff });
        break;
      case 'far':
        g.rect(-5, -1, 2, 2).rect(-1, -1, 2, 2).rect(3, -1, 2, 2).fill(0xffffff);
        break;
      case 'laneH':
        g.rect(-7, -1, 14, 3).fill(0xffffff);
        break;
      case 'laneV':
        g.rect(-1, -7, 3, 14).fill(0xffffff);
        break;
      case 'diag':
        g.moveTo(-5, -5)
          .lineTo(5, 5)
          .moveTo(5, -5)
          .lineTo(-5, 5)
          .stroke({ width: 2, color: 0xffffff });
        break;
      case 'basic':
        g.rect(-4, -5, 3, 3).fill({ color: 0xffffff, alpha: 0.6 });
        break;
    }
  } else if (cell.t === 'booster') {
    const hex = COLOR_HEX[cell.color];
    g.roundRect(-11, -11, 22, 22, 4).fill(0x2a2a3a).stroke({ width: 2, color: hex });
    switch (cell.kind) {
      case 'rocketH':
        g.poly([-9, 0, -3, -5, -3, 5]).poly([9, 0, 3, -5, 3, 5]).rect(-3, -2, 6, 4).fill(hex);
        break;
      case 'rocketV':
        g.poly([0, -9, -5, -3, 5, -3]).poly([0, 9, -5, 3, 5, 3]).rect(-2, -3, 4, 6).fill(hex);
        break;
      case 'bomb':
        g.circle(0, 1, 7).fill(0x101010).stroke({ width: 2, color: hex });
        g.rect(-1, -9, 2, 4).fill(0xffa040);
        break;
      case 'propeller':
        g.rect(-9, -2, 18, 4).rect(-2, -9, 4, 18).fill(hex);
        g.circle(0, 0, 3).fill(0xffffff);
        break;
    }
  } else if (cell.t === 'lightball') {
    g.circle(0, 0, 10).fill(0xffffff).stroke({ width: 2, color: dark });
    poly(g, 5, 6, -Math.PI / 2).fill(0xffe070);
  } else {
    poly(g, 6, 10, Math.PI / 6)
      .fill(0x8fd8d0)
      .stroke({ width: 2, color: dark });
    const n = Math.min(cell.charge, 6);
    for (let i = 0; i < n; i++) g.rect(-7 + i * 2.5, 6, 1.5, 2);
    if (n > 0) g.fill(0x15151f);
  }
  return g;
}

function drawOverlay(o: Overlay): GraphicsContext {
  const g = new GraphicsContext();
  if (o.type === 'lock') {
    g.rect(-12, -3, 24, 2).rect(-12, 2, 24, 2).fill(0x9090a0);
    g.roundRect(-4, -6, 8, 9, 2).fill(0xc0c0d0);
  } else if (o.type === 'freeze') {
    const a = 0.25 + 0.15 * Math.min(o.layers, 3);
    g.roundRect(-12, -12, 24, 24, 3).fill({ color: 0xbfe8ff, alpha: a });
    g.roundRect(-12, -12, 24, 24, 3).stroke({ width: 1, color: 0xffffff });
  } else {
    g.moveTo(-8, -8).lineTo(8, 8).moveTo(8, -8).lineTo(-8, 8).stroke({ width: 3, color: 0xff2040 });
  }
  return g;
}

export function orbContext(cell: Cell): GraphicsContext {
  const k = orbKey(cell) + (cell.t === 'essence' ? `.${cell.charge}` : '');
  let ctx = cache.get(k);
  if (!ctx) {
    ctx = drawOrb(cell);
    cache.set(k, ctx);
  }
  return ctx;
}

export function overlayContext(o: Overlay): GraphicsContext {
  const k = overlayKey(o);
  let ctx = cache.get(k);
  if (!ctx) {
    ctx = drawOverlay(o);
    cache.set(k, ctx);
  }
  return ctx;
}

/** Màu đại diện của một viên cho đường link (light ball/ngọc ấn: trắng/ngọc). */
export function cellTint(cell: Cell | null): number {
  if (!cell) return 0xffffff;
  if (cell.t === 'orb' || cell.t === 'booster') return COLOR_HEX[cell.color];
  return cell.t === 'lightball' ? 0xffffff : 0x8fd8d0;
}
